import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useToasts } from 'react-toast-notifications'
import Joi from 'joi'

import MainModal from '../../../../../components/dashboard/modal/MainModal'
import ModalHead from '../../../../../components/dashboard/modal/ModalHead'

import Tabs from '../../../../../components/Tabs'

import { P } from '../../../../../style/Typography.style'
import Flex from '../../../../../components/layouts/Flex'

import PersonalInformation from './PersonalInformation'
import EducationalBackground from './EducationalBackground'
import JobExperience from './JobExperience'
import ApplicantSkills from './ApplicantSkills'
import CharacterReference from './CharacterReference'

import { setModal } from '../../../../../store/actions/listsActions'
import { setActiveTab } from '../../../../../store/actions/tabActions'
import { updateTalent } from '../../../../../store/actions/talentActions'

const EditApplicantDetails = () => {
  const { addToast } = useToasts()
  const dispatch = useDispatch()

  const { selectedInterview } = useSelector((state) => state.requisitions)

  const { talent } = useSelector((state) => state.talents)
  const { activeTab } = useSelector((state) => state.tabs)
  const { modal } = useSelector((state) => state.lists)

  const [loading, setLoading] = useState(false)
  const [fieldErrors, setFieldErrors] = useState({})

  const [personalInformation, setPersonalInformation] = useState({})
  const [educationalBackground, setEducationalBackground] = useState({
    currentStatus: 'Graduated',
    from: new Date().toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0],
  })
  const [jobExperience, setJobExperience] = useState({
    workType: 'Permanent Work From Home',
    experienceCurrentStatus: 'No longer employed',
    experienceFrom: new Date().toISOString().split('T')[0],
    experienceTo: new Date().toISOString().split('T')[0],
  })
  const [skill, setSkill] = useState({})
  const [characterReference, setCharacterReference] = useState({
    name: '',
    email: '',
    phone: '',
  })

  const [educationalBackgrounds, setEducationalBackgrounds] = useState([])
  const [jobExperiences, setJobExperiences] = useState([])
  const [characterReferences, setCharacterReferences] = useState([])

  const validatePersonalInformation = (payload) => {
    const personalInformationSchema = Joi.object({
      firstName: Joi.string().label('First Name').required(),
      middleName: Joi.string().label('Middle Name').required(),
      lastName: Joi.string().label('Last Name').required(),
      professionalSummary: Joi.string()
        .label('Professional Summary')
        .allow(null, '') // allow empty string or null
        .custom((value, helpers) => {
          // if value is empty, skip validation
          if (!value) return value

          // strip HTML tags
          const plainText = value.replace(/<[^>]*>/g, '').trim()

          // if there's no meaningful content, allow it (since optional)
          if (!plainText) return value

          // otherwise return the cleaned value or pass it through
          return value
        })
        .optional(),
      contactNumber: Joi.string().label('Contact Number').allow(''),
      country: Joi.object({
        label: Joi.string().required(),
        value: Joi.string().required(),
      }).required(),
      city: Joi.object({
        label: Joi.string().required(),
        value: Joi.string().required(),
      }).required(),
      address: Joi.string().label('Address').required(),
      zipCode: Joi.string().label('ZIP Code').required(),
    })

    const result = personalInformationSchema.validate(payload, {
      abortEarly: false,
    })

    if (result.error) {
      const { error } = result
      const fieldNames = Object.keys(payload)
      const updatedErrors = fieldNames.reduce((acc, fieldName) => {
        acc[fieldName] =
          error?.details
            .find((detail) => detail.context.key === fieldName)
            ?.message.replace(/"/g, '') || ''
        return acc
      }, {})

      setFieldErrors((prev) => ({
        ...prev,
        personalInformation: { ...updatedErrors },
      }))
    } else {
      setFieldErrors((prev) => ({
        ...prev,
        personalInformation: {},
      }))
    }
  }

  const validateSkills = (payload) => {
    const skillSchema = Joi.object({
      technicalSkills: Joi.array()
        .items(
          Joi.object({
            label: Joi.string().required(),
            value: Joi.alternatives(Joi.string(), Joi.number()).required(),
            experience: Joi.alternatives(Joi.string(), Joi.number()).required(),
          })
        )
        .label('Technical Skills')
        .min(1)
        .required(),

      softSkills: Joi.array()
        .items(
          Joi.object({
            label: Joi.string().required(),
            value: Joi.string().required(),
          })
        )
        .label('Soft Skills')
        .min(1)
        .required(),

      professionalHeadlines: Joi.array()
        .items(
          Joi.object({
            label: Joi.string().required(),
            value: Joi.alternatives(Joi.string(), Joi.number()).required(),
          })
        )
        .label('Professional Headlines')
        .min(1)
        .required(),
    })

    const result = skillSchema.validate(payload, {
      abortEarly: false,
    })

    if (result.error) {
      const { error } = result
      const fieldNames = Object.keys(payload)
      const updatedErrors = fieldNames.reduce((acc, fieldName) => {
        acc[fieldName] =
          error?.details
            .find((detail) => detail.context.key === fieldName)
            ?.message.replace(/"/g, '') || ''
        return acc
      }, {})

      setFieldErrors((prev) => ({
        ...prev,
        skill: { ...updatedErrors },
      }))
    } else {
      setFieldErrors((prev) => ({
        ...prev,
        skill: {},
      }))
    }
  }

  const handleTabChange = () => {
    const currentTab = activeTab?.editApplicantDetails

    if (currentTab === 'personalInformation') {
      validatePersonalInformation(personalInformation)
    } else if (currentTab === 'applicantSkills') validateSkills(skill)
  }

  const countNonEmptyFields = (obj) => {
    if (!obj || typeof obj !== 'object') return 0

    return Object.values(obj).reduce((count, value) => {
      return count + (typeof value === 'string' && value.trim() !== '' ? 1 : 0)
    }, 0)
  }

  const handleUpdateApplicant = async () => {
    try {
      setLoading(true)

      const formattedPersonalInformation = {
        first_name: personalInformation?.firstName,
        middle_name: personalInformation?.middleName,
        last_name: personalInformation?.lastName,
        name: `${personalInformation?.firstName} ${personalInformation?.middleName} ${personalInformation?.lastName}`,
        country: personalInformation?.country?.value,
        city: personalInformation?.city?.value,
        phone_number: personalInformation?.contactNumber,
        professional_summary: personalInformation?.professionalSummary,
      }

      const professionalHeadlines = skill?.professionalHeadlines?.map((e) => ({
        id: e.value,
        item: e.label,
        status: 'moved',
      }))

      const formattedSkills = [
        skill?.technicalSkills?.map((e) => ({
          id: e.value,
          skill: e.label,
          experience: e.experience,
        })),
        skill?.softSkills?.map((e) => ({
          value: e.value,
          label: e.label,
        })),
      ]

      const meta = {
        ZIPCode: personalInformation?.zipCode,
        streetAddress: personalInformation?.address,
        educationalData: (educationalBackgrounds || [])
          .map((e) => ({
            ...e,
            from: new Date(e.from).toISOString(),
            to: new Date(e.to).toISOString(),
          }))
          .sort((a, b) => new Date(a.to) - new Date(b.to)), // ascending by "to"
        experienceState: (jobExperiences || [])
          .map((e) => ({
            ...e,
            experienceFrom: new Date(e.experienceFrom).toISOString(),
            experienceTo: new Date(e.experienceTo).toISOString(),
          }))
          .sort((a, b) => new Date(a.experienceTo) - new Date(b.experienceTo)), // ascending by "experienceTo"
        fullSkills: formattedSkills,
      }

      const formattedCharacterReferences = characterReferences?.map((e) => ({
        id: e.id,
        crName: e.name,
        crEmail: e.email,
        crPhone: e.phone,
      }))

      const payload = {
        ...formattedPersonalInformation,
        meta: JSON.stringify(meta),
        professional_headline: JSON.stringify(professionalHeadlines),
        character_reference: JSON.stringify(formattedCharacterReferences),
      }

      dispatch(updateTalent(talent?.user_id, payload))

      addToast(`Applicant information updated successfully.`, {
        appearance: 'info',
        autoDismiss: true,
      })

      setLoading(false)

      handleClose()
    } catch (error) {
      setLoading(false)
      addToast('Something went wrong when trying to update applicant details', {
        appearance: 'error',
        autoDismiss: true,
      })
    }
  }

  const handlePersonalInformationChange = (updatedInfo) => {
    setPersonalInformation((prevInfo) => ({
      ...prevInfo,
      ...updatedInfo,
    }))
  }

  const handleEducationalBackgroundChange = (updatedInfo) => {
    setEducationalBackground((prevInfo) => ({
      ...prevInfo,
      ...updatedInfo,
    }))
  }

  const handleJobExperienceChange = (updatedInfo) => {
    setJobExperience((prevInfo) => ({
      ...prevInfo,
      ...updatedInfo,
    }))
  }

  const handleSkillChange = (name, value) => {
    setSkill((prevInfo) => ({
      ...prevInfo,
      [name]: value,
    }))
  }

  const handleCharacterReferenceChange = (updatedInfo) => {
    setCharacterReference((prevInfo) => ({
      ...prevInfo,
      ...updatedInfo,
    }))
  }

  const handleClose = () => {
    const updatedModals = modal.filter((e) => e !== 'editApplicantDetails')
    dispatch(setModal(updatedModals))
  }

  useEffect(() => {
    // set active tab to personal information on open
    dispatch(
      setActiveTab({
        group: 'editApplicantDetails',
        name: 'personalInformation',
      })
    )

    let fetchedMeta = talent?.meta
    fetchedMeta = JSON.parse(fetchedMeta)

    // load personal information
    const updatedPersonalInformation = {
      firstName: talent?.first_name,
      middleName: talent?.middle_name,
      lastName: talent?.last_name,
      professionalSummary: talent?.professional_summary,
      contactNumber: talent?.phone_number,
      country: {
        value: talent?.country,
        label: talent?.country,
      },
      city: {
        value: talent?.city,
        label: talent?.city,
      },
      address: fetchedMeta?.streetAddress,
      zipCode: fetchedMeta?.ZIPCode,
    }
    setPersonalInformation(updatedPersonalInformation || {})

    // load educational background
    setEducationalBackgrounds(fetchedMeta?.educationalData || [])

    // load job experiences
    setJobExperiences(fetchedMeta?.experienceState || [])

    // load skills
    const fetchedSkills = fetchedMeta?.fullSkills
    let fetchedProfessionalHeadlines = talent?.professional_headline
    fetchedProfessionalHeadlines = JSON.parse(fetchedProfessionalHeadlines)
    fetchedProfessionalHeadlines = fetchedProfessionalHeadlines?.map((e) => ({
      label: e.item,
      value: e.id,
    }))

    const updatedSkills = {
      technicalSkills: fetchedSkills[0]?.map((e) => ({
        label: e.skill,
        value: e.id,
        experience: e.experience,
      })),
      softSkills: fetchedSkills[1],
      professionalHeadlines: fetchedProfessionalHeadlines,
    }
    setSkill(updatedSkills)

    let fetchedCharacterReferences = talent?.character_reference
    fetchedCharacterReferences = JSON.parse(fetchedCharacterReferences)
    fetchedCharacterReferences = fetchedCharacterReferences?.map((e) => ({
      id: e.id,
      name: e.crName,
      email: e.crEmail,
      phone: e.crPhone,
    }))

    // load character references
    setCharacterReferences(fetchedCharacterReferences || [])

    // check if personal information and skill tabs have missing fields
    validatePersonalInformation(updatedPersonalInformation)
    validateSkills(updatedSkills)
  }, [])

  return (
    <MainModal
      buttonText="Update Applicant Details"
      loading={loading}
      onClick={handleUpdateApplicant}
      info
      header={
        <ModalHead
          name={'Edit Applicant Details'}
          role={selectedInterview.role}
          onClose={handleClose}
        />
      }
      size="large"
      maxWidth="720px"
      onClose={handleClose}
    >
      <Tabs
        onBeforeTabClick={handleTabChange}
        tabList={[
          {
            name: (
              <Flex>
                <P
                  color={
                    countNonEmptyFields(fieldErrors?.personalInformation) > 0 &&
                    '#ff0000'
                  }
                  cursor="pointer"
                >
                  Personal Information
                </P>
              </Flex>
            ),
            value: 'personalInformation',
          },
          {
            name: (
              <Flex>
                <P
                  color={
                    !educationalBackgrounds ||
                    educationalBackgrounds.length === 0
                      ? '#ff0000'
                      : undefined
                  }
                  cursor="pointer"
                >
                  Educational Background
                </P>
              </Flex>
            ),
            value: 'educationalBackground',
          },
          {
            name: (
              <Flex>
                <P
                  color={
                    !jobExperiences || jobExperiences.length === 0
                      ? '#ff0000'
                      : undefined
                  }
                  cursor="pointer"
                >
                  Job Experience
                </P>
              </Flex>
            ),
            value: 'jobExperience',
          },
          {
            name: (
              <Flex>
                <P
                  color={
                    countNonEmptyFields(fieldErrors?.skill) > 0 && '#ff0000'
                  }
                  cursor="pointer"
                >
                  Skills
                </P>
              </Flex>
            ),
            value: 'applicantSkills',
          },
          {
            name: (
              <Flex>
                <P
                  color={
                    !characterReferences || characterReferences.length === 0
                      ? '#ff0000'
                      : undefined
                  }
                  cursor="pointer"
                >
                  Character References
                </P>
              </Flex>
            ),
            value: 'characterReference',
          },
        ]}
        tabGroup="editApplicantDetails"
      />

      {activeTab.editApplicantDetails === 'personalInformation' && (
        <PersonalInformation
          details={personalInformation}
          onChange={handlePersonalInformationChange}
          fieldErrors={fieldErrors?.personalInformation}
        />
      )}
      {activeTab.editApplicantDetails === 'educationalBackground' && (
        <EducationalBackground
          educationalBackground={educationalBackground}
          educationalBackgrounds={educationalBackgrounds}
          setEducationalBackground={setEducationalBackground}
          setEducationalBackgrounds={setEducationalBackgrounds}
          fieldErrors={fieldErrors?.educationalBackground}
          setFieldErrors={setFieldErrors}
          onChange={handleEducationalBackgroundChange}
        />
      )}
      {activeTab.editApplicantDetails === 'jobExperience' && (
        <JobExperience
          jobExperience={jobExperience}
          jobExperiences={jobExperiences}
          setJobExperience={setJobExperience}
          setJobExperiences={setJobExperiences}
          fieldErrors={fieldErrors?.jobExperience}
          setFieldErrors={setFieldErrors}
          onChange={handleJobExperienceChange}
        />
      )}

      {activeTab.editApplicantDetails === 'applicantSkills' && (
        <ApplicantSkills
          skill={skill}
          onChange={handleSkillChange}
          fieldErrors={fieldErrors?.skill}
        />
      )}
      {activeTab.editApplicantDetails === 'characterReference' && (
        <CharacterReference
          characterReference={characterReference}
          characterReferences={characterReferences}
          setCharacterReference={setCharacterReference}
          setCharacterReferences={setCharacterReferences}
          fieldErrors={fieldErrors?.characterReference}
          setFieldErrors={setFieldErrors}
          onChange={handleCharacterReferenceChange}
        />
      )}
    </MainModal>
  )
}

export default EditApplicantDetails
